import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Layers, Map as MapIcon } from 'lucide-react';
import {
  Map,
  MapCircle,
  MapFitBounds,
  MapInvalidateSize,
  MapMarker,
  MapPolyline,
  MapTileLayer,
  MapZoomControl,
  createLabeledMapIcon,
} from '../UI/map';
import { Card, CardContent } from '../UI/card';
import { Badge } from '../UI/badge';
import { Button } from '../UI/button';
import { EmptyState } from '../UI/empty-state';
import { Skeleton } from '../UI/skeleton';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import TenantMapDetailPanel from './TenantMapDetailPanel';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { themeClasses } from '../../config/themeClasses';
import {
  buildTenantNetworkRoutes,
  enrichMarkersWithSupplierRisk,
  getMarkerBreadcrumb,
} from '../../utils/tenantMapModel';
import { markerColorFromRisk } from '../../utils/watchlistMapModel';
import { cn } from '../../lib/utils';

const DEFAULT_CENTER = [20, 0];

const MARKER_COLORS = {
  own: colors.secondary[500],
  supplier: colors.danger[500],
  geography: colors.info[500],
};

const riskLevelColor = (level) => {
  const normalized = String(level || '').toLowerCase();
  if (normalized.includes('high') || normalized.includes('critical')) return colors.danger[500];
  if (normalized.includes('medium') || normalized.includes('moderate')) return colors.warning[500];
  if (normalized.includes('low')) return colors.success[500];
  return colors.gray[500];
};

function TenantSupplyChainMap({
  markers = [],
  routes = [],
  bounds = [],
  loading = false,
  enhancing = false,
  geocoding = false,
  selectedMarkerId,
  onMarkerSelect,
  onGoToDataImport,
  overallRiskScore,
  profile,
  tenantSuppliers = [],
  companies = [],
}) {
  const isEnhancing = enhancing || geocoding;
  const { t } = useLocale();
  const [layers, setLayers] = useState({
    own: true,
    supplier: true,
    geography: false,
    logistics: false,
    graph: false,
    network: true,
  });
  const [selectedRouteId, setSelectedRouteId] = useState(null);

  const companyName = profile?.name || '';

  const enrichedMarkers = useMemo(
    () => enrichMarkersWithSupplierRisk(markers, tenantSuppliers, companies),
    [markers, tenantSuppliers, companies]
  );

  const networkRoutes = useMemo(
    () => buildTenantNetworkRoutes(enrichedMarkers, companyName),
    [enrichedMarkers, companyName]
  );

  const allRoutes = useMemo(
    () => [...routes, ...networkRoutes],
    [routes, networkRoutes]
  );

  const selectedMarker = useMemo(
    () => enrichedMarkers.find((marker) => marker.id === selectedMarkerId) || null,
    [enrichedMarkers, selectedMarkerId]
  );

  const selectedRoute = useMemo(
    () => allRoutes.find((route) => route.id === selectedRouteId) || null,
    [allRoutes, selectedRouteId]
  );

  const visibleMarkers = useMemo(
    () =>
      enrichedMarkers.filter((marker) => {
        if (marker.type === 'own') return layers.own;
        if (marker.type === 'supplier') return layers.supplier;
        if (marker.type === 'geography') return layers.geography;
        return true;
      }),
    [enrichedMarkers, layers]
  );

  const visibleRoutes = useMemo(
    () =>
      allRoutes.filter((route) => {
        if (route.type === 'logistics') return layers.logistics;
        if (route.type === 'graph') return layers.graph;
        if (route.type === 'network') return layers.network;
        return true;
      }),
    [allRoutes, layers]
  );

  const highlightedRouteIds = useMemo(() => {
    if (!selectedMarker) return new Set();
    return new Set(
      visibleRoutes
        .filter(
          (route) =>
            route.fromMarkerId === selectedMarker.id || route.toMarkerId === selectedMarker.id
        )
        .map((route) => route.id)
    );
  }, [selectedMarker, visibleRoutes]);

  const fitBounds = useMemo(() => {
    const points = [];
    visibleMarkers.forEach((marker) => points.push(marker.position));
    visibleRoutes.forEach((route) => (route.positions || []).forEach((point) => points.push(point)));
    return points.length > 0 ? points : bounds;
  }, [visibleMarkers, visibleRoutes, bounds]);

  const breadcrumb = useMemo(
    () => getMarkerBreadcrumb(selectedMarker, companyName),
    [selectedMarker, companyName]
  );

  useEffect(() => {
    if (selectedMarkerId || enrichedMarkers.length === 0) return;
    const firstOwn = enrichedMarkers.find((marker) => marker.type === 'own');
    const fallback = firstOwn || enrichedMarkers.find((marker) => marker.type === 'supplier');
    if (fallback) onMarkerSelect?.(fallback.id);
  }, [enrichedMarkers, selectedMarkerId, onMarkerSelect]);

  const toggleLayer = (key) => {
    setLayers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMarkerClick = (markerId) => {
    setSelectedRouteId(null);
    onMarkerSelect?.(markerId);
  };

  const handleRouteClick = (routeId) => {
    setSelectedRouteId(routeId);
  };

  const handleViewNodeData = (marker) => {
    if (marker?.nodeKey) {
      onMarkerSelect?.(marker.id, { openNodeTab: marker.nodeKey });
    }
  };

  const showDataOverlay = loading && enrichedMarkers.length === 0 && allRoutes.length === 0;

  if (!loading && !isEnhancing && enrichedMarkers.length === 0 && allRoutes.length === 0) {
    return (
      <EmptyState
        icon={MapIcon}
        title={t('tenantMapEmptyTitle')}
        description={t('tenantMapEmptyDescription')}
        actionLabel={t('tenantMapEmptyCta')}
        onAction={onGoToDataImport}
        className="min-h-[520px]"
      />
    );
  }

  return (
    <Card className={cn(themeClasses.appKit.sectionShell, 'overflow-hidden')}>
      <CardContent className="p-0">
        <div className="relative h-[min(72vh,640px)] min-h-[520px] w-full">
          {showDataOverlay ? (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-background-card/80">
              <div className="flex flex-col items-center gap-2">
                <Skeleton className="h-8 w-48" />
                <p className="text-sm text-text-secondary">{t('tenantMapLoading')}</p>
              </div>
            </div>
          ) : null}

          {isEnhancing ? (
            <div className="absolute right-4 top-4 z-20">
              <Badge variant="secondary">{t('tenantMapGeocoding')}</Badge>
            </div>
          ) : null}

          {selectedMarker ? (
            <div className="absolute left-4 top-4 z-20 max-w-[min(70%,320px)]">
              <div className="rounded-lg border border-border-light bg-background-card/90 px-3 py-2 text-xs text-text-secondary shadow-md backdrop-blur-sm">
                <span className="text-text-primary">{breadcrumb.location}</span>
                {breadcrumb.name ? (
                  <>
                    <span className="mx-1.5 text-text-tertiary">{t('tenantMapBreadcrumbSeparator')}</span>
                    <span className="font-medium text-text-primary">{breadcrumb.name}</span>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="absolute bottom-4 left-4 z-20">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="secondary" size="sm" className="gap-2 shadow-md">
                  <Layers className="h-4 w-4" />
                  {t('tenantMapLayersTitle')}
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {[
                  { key: 'own', labelKey: 'tenantMapLayerOwn' },
                  { key: 'supplier', labelKey: 'tenantMapLayerSupplier' },
                  { key: 'network', labelKey: 'tenantMapLayerNetwork' },
                  { key: 'geography', labelKey: 'tenantMapLayerGeography' },
                  { key: 'logistics', labelKey: 'tenantMapLayerLogistics' },
                  { key: 'graph', labelKey: 'tenantMapLayerGraph' },
                ].map((layer) => (
                  <DropdownMenuCheckboxItem
                    key={layer.key}
                    checked={layers[layer.key]}
                    onCheckedChange={() => toggleLayer(layer.key)}
                  >
                    {t(layer.labelKey)}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {selectedRoute?.type === 'network' && companyName ? (
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 max-w-[280px] -translate-x-1/2 -translate-y-1/2">
              <div className="rounded-lg border border-secondary-500/40 bg-background-card/95 px-3 py-2 text-center text-xs text-text-primary shadow-lg backdrop-blur-sm">
                {t('tenantMapRouteRelationshipTooltip', { company: companyName })}
              </div>
            </div>
          ) : null}

          <Map center={DEFAULT_CENTER} zoom={2} className="h-full w-full rounded-none border-0">
            <MapTileLayer variant="dark" />
            <MapZoomControl />
            <MapInvalidateSize
              invalidateKey={`${enrichedMarkers.length}-${allRoutes.length}-${isEnhancing}-${selectedMarkerId}`}
            />
            {fitBounds.length > 0 ? <MapFitBounds bounds={fitBounds} padding={[72, 72]} /> : null}

            {visibleRoutes.map((route) => {
              const isHighlighted =
                selectedRouteId === route.id || highlightedRouteIds.has(route.id);
              const isNetwork = route.type === 'network';
              return (
                <MapPolyline
                  key={route.id}
                  positions={route.positions}
                  color={isNetwork ? colors.secondary[500] : colors.brand[500]}
                  opacity={isHighlighted ? 0.95 : isNetwork ? 0.55 : 0.35}
                  weight={isHighlighted ? 3.5 : 2}
                  dashed={isNetwork}
                  eventHandlers={{
                    click: () => handleRouteClick(route.id),
                  }}
                />
              );
            })}

            {visibleMarkers
              .filter((marker) => marker.type !== 'geography')
              .map((marker) => {
                const isSelected = selectedMarkerId === marker.id;
                const markerColor = isSelected
                  ? colors.secondary[500]
                  : marker.riskScore != null && Number(marker.riskScore) > 0
                    ? markerColorFromRisk(marker.riskScore)
                    : MARKER_COLORS[marker.type] || colors.brand[500];
                const icon = createLabeledMapIcon({
                  color: markerColor,
                  label: marker.title,
                  selected: isSelected,
                  variant: marker.type === 'own' ? 'own' : 'default',
                });

                return (
                  <MapMarker
                    key={marker.id}
                    position={marker.position}
                    icon={icon}
                    eventHandlers={{
                      click: () => handleMarkerClick(marker.id),
                    }}
                  />
                );
              })}

            {layers.geography
              ? visibleMarkers
                  .filter((marker) => marker.type === 'geography')
                  .map((marker) => (
                    <MapCircle
                      key={marker.id}
                      center={marker.position}
                      radius={140000}
                      color={riskLevelColor(marker.riskLevel)}
                      eventHandlers={{
                        click: () => handleMarkerClick(marker.id),
                      }}
                    />
                  ))
              : null}
          </Map>

          {selectedMarker ? (
            <TenantMapDetailPanel
              marker={selectedMarker}
              markers={enrichedMarkers}
              companyName={companyName}
              overallRiskScore={overallRiskScore}
              onClose={() => onMarkerSelect?.(null)}
              onMarkerSelect={handleMarkerClick}
              onViewNodeData={handleViewNodeData}
            />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export default TenantSupplyChainMap;
